import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Unlock, User, Mail, Search, Check, X, Users, ClipboardList, Newspaper, ChevronDown, ChevronUp, Download, Flame, Leaf, Waves, UserPlus, Eye, EyeOff, Activity, KeyRound, Trash2, BarChart3, RotateCcw, CalendarClock, MessageCircle, NotebookPen, Save, Loader2 } from 'lucide-react';
import { sections as bodySections, scaleOptions as bodyScale, deeperDive as bodyDeeperDive } from '../data/bodyAssessment';
import { metabolicQuestions, metabolicRhythmLabels, metabolicLetterMeaning, lifestyleDomains, lifestyleAnswerLabels, scoreLifestyleValue, getLifestyleDomainStatus } from '../data/quizMeta';
import { journalCategories, journalItemLabels } from '../data/journalChecklist';
import PractitionerWorksheet from '../components/PractitionerWorksheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const quizTypeLabel = (quizType) => {
  if (quizType === 'metabolic_rhythm') return 'Metabolic Rhythm Quiz';
  if (quizType === 'wellness_pattern_assessment') return 'What Is Your Body Asking For?';
  if (quizType === 'food_preferences') return 'Food Preferences';
  return 'Vitality Potential Tool';
};

const lifestyleStatusColor = (s) =>
  s === 'Dysregulated' ? 'text-rose-600' : s === 'Compensating' ? 'text-orange-600' : 'text-emerald-600';

const bodyScaleLabel = (v) => (bodyScale.find((o) => o.value === Number(v))?.label) ?? String(v);

const programs = [
  { id: 'back-reset', title: 'The Back Reset Program' },
  { id: 'fascia-reset', title: '28 Day Stress Reset' },
  { id: 'terrain', title: 'SLOW WELLNESS' },
  { id: 'crap-cleanse', title: '14-Day C.R.A.P. Cleanse (Full)' },
  { id: 'crap-cleanse-intro', title: '14-Day C.R.A.P. Cleanse (Intro Only)' },
  { id: 'feel-good-detox', title: '21-Day Feel Good Detox (Full)' },
  { id: 'feel-good-detox-intro', title: '21-Day Feel Good Detox (Intro Only)' },
  { id: 'flex-detox', title: 'The Flex Detox Program' },
  { id: 'double-down', title: 'The Double Down Program' },
  { id: 'vip', title: 'V.I.P. Video Library (Private Clients)' },
  { id: 'dignity-house', title: 'Dignity House (Team Program)' },
  { id: 'sbrc', title: 'SBRC (Team Program)' },
  { id: 'fast-brain', title: 'The Spicy Brain Survival Guide' },
  { id: 'fifty2-tuesdays', title: 'Fifty2 Tuesdays (Full Year)' },
  { id: 'life-ninja-game', title: 'The Life Ninja Game (Full Journey)' },
  { id: 'surviving-to-thriving', title: 'From Surviving to Thriving (Full Journey)' },
  { id: 'four-pillars', title: 'The Four Pillars (Full Journey)' },
];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [pollVotes, setPollVotes] = useState([]);
  const [comments, setComments] = useState([]);
  const [pollConfig, setPollConfig] = useState({ is_closed: false, final_message: '' });
  const [pollConfigDraft, setPollConfigDraft] = useState({ is_closed: false, final_message: '' });
  const [pollConfigSaving, setPollConfigSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  const [allUsersExpanded, setAllUsersExpanded] = useState(false);
  const toggleUserExpanded = (email) => setExpandedUsers((prev) => ({ ...prev, [email]: !prev[email] }));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedResults, setExpandedResults] = useState({});
  const [worksheetResult, setWorksheetResult] = useState(null);

  // Client Journal modal state
  const [journalUser, setJournalUser] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  
  // Add User Modal State
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', unlocked_programs: [] });
  const [showPassword, setShowPassword] = useState(false);
  const [addingUser, setAddingUser] = useState(false);

  // Reset Password & Delete User State
  const [resetPasswordFor, setResetPasswordFor] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [deleteConfirmFor, setDeleteConfirmFor] = useState(null);

  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, password: adminPassword }),
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchAllData();
      } else {
        setError('Invalid admin credentials');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    fetchUsers();
    fetchSubscribers();
    fetchQuizResults();
    fetchUserActivity();
    fetchIntakes();
    fetchInquiries();
    fetchPollVotes();
    fetchAnalytics();
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/newsletter/subscribers?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data.subscribers || []);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
    }
  };

  const fetchQuizResults = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/quiz-results?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setQuizResults(data.results || []);
      }
    } catch (err) {
      console.error('Error fetching quiz results:', err);
    }
  };

  const fetchUserActivity = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/user-activity?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setUserActivity(data.summaries || []);
      }
    } catch (err) {
      console.error('Error fetching user activity:', err);
    }
  };

  const fetchIntakes = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/new-client-intakes?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setIntakes(data.intakes || []);
      }
    } catch (err) {
      console.error('Error fetching intakes:', err);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/inquiries?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setInquiries(data.inquiries || []);
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
    }
  };

  const toggleInquiryResponded = async (inquiryId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/inquiries/${inquiryId}/responded?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        const data = await response.json();
        setInquiries((prev) => prev.map((i) => (i.id === inquiryId ? { ...i, responded: data.responded } : i)));
      }
    } catch (err) {
      console.error('Error toggling inquiry:', err);
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/inquiries/${inquiryId}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setInquiries((prev) => prev.filter((i) => i.id !== inquiryId));
      }
    } catch (err) {
      console.error('Error deleting inquiry:', err);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/flex-detox/comments?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment permanently?')) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/flex-detox/comments/${commentId}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (response.ok) setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };


  const fetchPollVotes = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/zoom-poll/votes?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`
      );
      if (response.ok) {
        const data = await response.json();
        setPollVotes(data.votes || []);
      }
    } catch (err) {
      console.error('Error fetching poll votes:', err);
    }
    // Also fetch the poll config so admin can close/open
    try {
      const cfgRes = await fetch(`${BACKEND_URL}/api/zoom-poll/config?poll_id=zoom-times-28-day`);
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        const nextCfg = { is_closed: !!cfg.is_closed, final_message: cfg.final_message || '' };
        setPollConfig(nextCfg);
        setPollConfigDraft(nextCfg);
      }
    } catch (err) {
      console.error('Error fetching poll config:', err);
    }
  };

  const savePollConfig = async () => {
    setPollConfigSaving(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/zoom-poll/config?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ poll_id: 'zoom-times-28-day', ...pollConfigDraft }),
        }
      );
      if (res.ok) {
        setPollConfig(pollConfigDraft);
        setSuccess(pollConfigDraft.is_closed ? 'Poll closed — public page now shows final times.' : 'Poll reopened — voting is live again.');
      } else {
        setError('Failed to update poll status.');
      }
    } catch {
      setError('Failed to update poll status.');
    }
    setPollConfigSaving(false);
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  const markIntakeRead = async (intakeId) => {
    try {
      await fetch(
        `${BACKEND_URL}/api/admin/new-client-intakes/${intakeId}/read?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'PATCH' }
      );
      setIntakes((prev) => prev.map((i) => (i.id === intakeId ? { ...i, read: true } : i)));
    } catch (err) {
      console.error('Error marking intake read:', err);
    }
  };

  const openJournal = async (u) => {
    setJournalUser(u);
    setJournalEntries([]);
    setNoteDrafts({});
    setJournalLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/journal/${u.id}`);
      const data = await res.json();
      setJournalEntries(data.entries || []);
      const drafts = {};
      (data.entries || []).forEach((e) => { drafts[e.id] = e.practitioner_notes || ''; });
      setNoteDrafts(drafts);
    } catch (e) {
      setError('Failed to load client journal.');
    }
    setJournalLoading(false);
  };

  const savePractitionerNote = async (entryId) => {
    setSavingNoteId(entryId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/journal/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practitioner_notes: noteDrafts[entryId] || '' }),
      });
      const updated = await res.json();
      setJournalEntries((prev) => prev.map((e) => (e.id === entryId ? updated : e)));
      setSuccess('Note saved.');
    } catch (e) {
      setError('Failed to save note.');
    }
    setSavingNoteId(null);
  };

  const adminRestartProgram = async (userId, userName, programId, programTitle) => {
    if (!window.confirm(
      `Restart "${programTitle}" for ${userName}?\n\nThis relocks every day and makes today Day 1. Notes, favorites, and snapshots are preserved.`
    )) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/program-enrollment/restart?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, program_id: programId }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setSuccess(`Restarted ${programTitle} for ${userName} — Day 1 is today (restart #${data.restart_count}).`);
      } else {
        setError('Failed to restart program.');
      }
    } catch {
      setError('Failed to restart program.');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  const adminSetStartDate = async (userEmail, userName, programId, programTitle) => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const suggested = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const input = window.prompt(
      `Set the START DATE for "${programTitle}" for ${userName}.\n\nFormat: YYYY-MM-DD\n\nTip: To unlock all 28 days right now, enter a date ~30 days ago.\nToday is ${todayIso}. Suggested: ${suggested}`,
      suggested
    );
    if (!input) return;
    const trimmed = input.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      setError('Invalid date format. Please use YYYY-MM-DD (e.g. 2026-04-01).');
      setTimeout(() => setError(''), 4000);
      return;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/program-enrollment/set-start-date?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_email: userEmail,
            program_id: programId,
            start_date: trimmed,
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        const startDay = new Date(data.started_at).toLocaleDateString();
        setSuccess(`Start date for ${userName} set to ${startDay} — ${programTitle}.`);
      } else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || 'Failed to set start date.');
      }
    } catch {
      setError('Failed to set start date.');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 4500);
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/analytics/summary`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const toggleProgramAccess = async (userEmail, programId, currentlyUnlocked) => {
    setSuccess('');
    setError('');

    try {
      const endpoint = currentlyUnlocked ? 'lock-program' : 'unlock-program';
      const response = await fetch(
        `${BACKEND_URL}/api/admin/${endpoint}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_email: userEmail, program_id: programId }),
        }
      );

      if (response.ok) {
        setSuccess(`Program ${currentlyUnlocked ? 'locked' : 'unlocked'} for ${userEmail}`);
        fetchUsers();
      } else {
        setError('Failed to update access');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setAddingUser(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/create-user?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUser),
        }
      );

      if (response.ok) {
        setSuccess(`User ${newUser.email} created successfully!`);
        setShowAddUser(false);
        setNewUser({ name: '', email: '', password: '', unlocked_programs: [] });
        fetchUsers();
      } else {
        let detail = '';
        try {
          const data = await response.json();
          detail = data.detail || '';
        } catch {
          detail = '';
        }
        if (detail.toLowerCase().includes('already registered')) {
          setError(`A user with ${newUser.email} already exists. Search for them in the list below and use "Unlock Programs" to give them access, or create the user with a different email.`);
        } else {
          setError(detail || `Could not create user (server returned ${response.status}). Please try again.`);
        }
      }
    } catch (err) {
      setError('Could not reach the server to create the user. Please check your connection and try again.');
    } finally {
      setAddingUser(false);
    }
  };

  const toggleNewUserProgram = (programId) => {
    setNewUser(prev => ({
      ...prev,
      unlocked_programs: prev.unlocked_programs.includes(programId)
        ? prev.unlocked_programs.filter(id => id !== programId)
        : [...prev.unlocked_programs, programId]
    }));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser(prev => ({ ...prev, password }));
  };

  const handleResetPassword = async (email) => {
    if (!newPasswordValue || newPasswordValue.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/reset-password?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_email: email, new_password: newPasswordValue }),
        }
      );
      if (res.ok) {
        setSuccess(`Password reset for ${email}`);
        setResetPasswordFor(null);
        setNewPasswordValue('');
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to reset password');
      }
    } catch {
      setError('Failed to reset password');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleDeleteUser = async (email) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/delete-user?user_email=${encodeURIComponent(email)}&admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setSuccess(`User ${email} deleted`);
        setUsers(prev => prev.filter(u => u.email !== email));
        setDeleteConfirmFor(null);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to delete user');
      }
    } catch {
      setError('Failed to delete user');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleSetUserActive = async (email, active) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/set-user-active?user_email=${encodeURIComponent(email)}&active=${active}&admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        setSuccess(`${email} ${active ? 'reactivated' : 'deactivated'}`);
        setUsers(prev => prev.map(u => u.email === email ? { ...u, active } : u));
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to update account status');
      }
    } catch {
      setError('Failed to update account status');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleDeleteSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter list?`)) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/newsletter-subscribers/${encodeURIComponent(email)}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setSubscribers(prev => prev.filter(s => s.email !== email));
        setSuccess(`${email} removed from newsletter`);
      } else {
        setError('Failed to remove subscriber');
      }
    } catch {
      setError('Failed to remove subscriber');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const toggleResultsSent = async (quizId, nextValue) => {
    setQuizResults(prev => prev.map(q => q.quiz_id === quizId ? { ...q, results_sent: nextValue } : q));
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/quiz-results/${quizId}/results-sent`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ admin_email: adminEmail, admin_password: adminPassword, results_sent: nextValue }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        setQuizResults(prev => prev.map(q => q.quiz_id === quizId ? { ...q, results_sent: data.results_sent, results_sent_at: data.results_sent_at } : q));
      } else {
        setQuizResults(prev => prev.map(q => q.quiz_id === quizId ? { ...q, results_sent: !nextValue } : q));
        setError('Failed to update results status');
        setTimeout(() => setError(''), 3000);
      }
    } catch {
      setQuizResults(prev => prev.map(q => q.quiz_id === quizId ? { ...q, results_sent: !nextValue } : q));
      setError('Failed to update results status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteQuizResult = async (quizId, label) => {
    if (!window.confirm(`Delete this quiz submission${label ? ` (${label})` : ''}? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/delete-quiz/${quizId}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setQuizResults(prev => prev.filter(q => q.quiz_id !== quizId));
        setSuccess('Quiz submission deleted');
      } else {
        setError('Failed to delete quiz submission');
      }
    } catch {
      setError('Failed to delete quiz submission');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleDeleteIntake = async (intakeId, intakeName) => {
    if (!window.confirm(`Delete intake from ${intakeName || 'this submission'}? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/new-client-intakes/${intakeId}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setIntakes(prev => prev.filter(i => i.id !== intakeId));
        setSuccess('Intake deleted');
      } else {
        setError('Failed to delete intake');
      }
    } catch {
      setError('Failed to delete intake');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const handleDeletePollVote = async (voteId, voteName) => {
    if (!window.confirm(`Delete poll vote from ${voteName || 'this voter'}? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/zoom-poll/vote/${voteId}?admin_email=${encodeURIComponent(adminEmail)}&admin_password=${encodeURIComponent(adminPassword)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setPollVotes(prev => prev.filter(v => v.id !== voteId));
        setSuccess('Poll vote deleted');
      } else {
        setError('Failed to delete poll vote');
      }
    } catch {
      setError('Failed to delete poll vote');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const copyEmailsToClipboard = () => {
    const emails = subscribers.map(s => s.email).join(', ');
    navigator.clipboard.writeText(emails);
    setSuccess('All emails copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const downloadCSV = (data, filename) => {
    let csv = '';
    if (filename.includes('subscribers')) {
      csv = 'Email,Source,Subscribed At\n';
      data.forEach(s => {
        csv += `${s.email},${s.source || 'unknown'},${s.subscribed_at || ''}\n`;
      });
    } else if (filename.includes('quiz')) {
      csv = 'Email,Quiz Type,Result,Date\n';
      data.forEach(r => {
        const resultSummary = r.quiz_type === 'metabolic_rhythm' ? r.result : 
          (typeof r.result === 'string' && r.result.includes('lifestyleLoadIndex') ? 
            `Load: ${JSON.parse(r.result).lifestyleLoadIndex}%` : r.result);
        csv += `${r.email},${r.quiz_type},${resultSummary},${r.created_at || ''}\n`;
      });
    } else if (filename.includes('poll')) {
      const esc = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      csv = 'Name,Email,Mon-Thu Times,Friday Times,Saturday Times,Sunday Times,Suggested Times,Notes,Submitted At\n';
      data.forEach(v => {
        const sat = [...(v.saturday_times || []), ...(v.weekend_times || [])].join(' | ');
        csv += [
          esc(v.name), esc(v.email),
          esc((v.weekday_times || []).join(' | ')),
          esc((v.friday_times || []).join(' | ')),
          esc(sat),
          esc((v.sunday_times || []).join(' | ')),
          esc(v.suggested_times),
          esc(v.notes), esc(v.submitted_at || '')
        ].join(',') + '\n';
      });
    } else if (filename.includes('intake')) {
      const esc = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      csv = 'Name,Email,Phone,Goals,Experience,Heard From,Waiver Consent,Submitted At\n';
      data.forEach(i => {
        csv += [
          esc(i.name), esc(i.email), esc(i.phone),
          esc(i.goals), esc(i.experience), esc(i.heard_from),
          i.consent_waiver ? 'Yes' : 'No',
          esc(i.submitted_at || '')
        ].join(',') + '\n';
      });
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleResultExpand = (id) => {
    setExpandedResults(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getResultIcon = (quizType, result) => {
    if (quizType === 'metabolic_rhythm') {
      if (result === 'grounded') return <Flame className="text-orange-500" size={20} />;
      if (result === 'light') return <Leaf className="text-emerald-500" size={20} />;
      return <Waves className="text-blue-500" size={20} />;
    }
    if (quizType === 'wellness_pattern_assessment') return <Activity className="text-terracotta" size={20} />;
    return <ClipboardList className="text-violet-500" size={20} />;
  };

  const getResultLabel = (quizType, result) => {
    if (quizType === 'metabolic_rhythm') {
      if (result === 'grounded') return 'Grounded Burner';
      if (result === 'light') return 'Light & Quick';
      return 'Steady Balanced';
    }
    if (quizType === 'wellness_pattern_assessment') {
      return result ? `Primary: ${result}` : 'Assessment complete';
    }
    if (typeof result === 'string' && result.includes('lifestyleLoadIndex')) {
      try {
        const parsed = JSON.parse(result);
        return `Load: ${parsed.lifestyleLoadIndex}% | Resilience: ${parsed.resilienceCapacity}%`;
      } catch {
        return result;
      }
    }
    return result;
  };

  const filteredUsers = users
    .filter(
      (user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      (a.name || a.email || '').toLowerCase().localeCompare((b.name || b.email || '').toLowerCase())
    );

  const filteredSubscribers = subscribers.filter(
    (sub) => sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredQuizResults = quizResults.filter(
    (result) => result.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredIntakes = intakes.filter((i) => {
    const q = searchTerm.toLowerCase();
    return (
      (i.email || '').toLowerCase().includes(q) ||
      (i.name || '').toLowerCase().includes(q) ||
      (i.goals || '').toLowerCase().includes(q) ||
      (i.phone || '').toLowerCase().includes(q)
    );
  });

  if (!isAuthenticated) {
    return (
      <div className="bg-cream min-h-screen pt-32 pb-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full mx-auto"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-forest" />
            </div>
            <h1 className="font-heading text-2xl text-forest">Admin Dashboard</h1>
            <p className="text-stone-600 font-body text-sm mt-1">
              Enter your admin credentials
            </p>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg mb-4 font-body text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-forest font-body font-medium mb-1 text-sm">
                Admin Email
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
                placeholder="admin@gwwithjill.com"
              />
            </div>

            <div>
              <label className="block text-forest font-body font-medium mb-1 text-sm">
                Admin Password
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
                className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-forest text-cream py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-terracotta font-body text-sm hover:underline">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen pt-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-2"
            >
              <ArrowLeft size={18} />
              <span className="font-body text-sm">Back to Home</span>
            </Link>
            <h1 className="font-heading text-3xl text-forest">Admin Dashboard</h1>
          </div>
          <button
            onClick={() => {
              setIsAuthenticated(false);
              setAdminEmail('');
              setAdminPassword('');
            }}
            className="text-stone-600 font-body text-sm hover:text-forest"
          >
            Sign Out
          </button>
        </div>

        {/* Alerts */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-emerald-100 text-emerald-700 px-4 py-3 rounded-lg mb-6 font-body flex items-center gap-2"
            >
              <Check size={18} />
              {success}
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-red-100 text-red-700 px-4 py-3 rounded-lg mb-6 font-body flex items-center gap-2"
            >
              <X size={18} />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'users'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
          >
            <Users size={16} />
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('newsletter')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'newsletter'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
          >
            <Newspaper size={16} />
            Newsletter ({subscribers.length})
          </button>
          <button
            onClick={() => setActiveTab('quizzes')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'quizzes'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
          >
            <ClipboardList size={16} />
            Quiz Results ({quizResults.length})
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'activity'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="activity-tab"
          >
            <Activity size={16} />
            User Activity ({userActivity.length})
          </button>
          <button
            onClick={() => setActiveTab('intakes')}
            className={`relative px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'intakes'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="intakes-tab"
          >
            <UserPlus size={16} />
            New Client Intakes ({intakes.length})
            {intakes.filter((i) => !i.read).length > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-terracotta text-white text-[10px] font-bold"
                data-testid="intakes-unread-badge"
              >
                {intakes.filter((i) => !i.read).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`relative px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'inquiries'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="inquiries-tab"
          >
            <UserPlus size={16} />
            Inquiries ({inquiries.length})
            {inquiries.filter((i) => !i.responded).length > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-terracotta text-white text-[10px] font-bold"
                data-testid="inquiries-unread-badge"
              >
                {inquiries.filter((i) => !i.responded).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('poll')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'poll'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="poll-tab"
          >
            <BarChart3 size={16} />
            Zoom Poll ({pollVotes.length})
          </button>
          <button
            onClick={() => { setActiveTab('analytics'); if (!analytics) fetchAnalytics(); }}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'analytics'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="analytics-tab"
          >
            <BarChart3 size={16} />
            Site Analytics
          </button>
          <button
            onClick={() => { setActiveTab('comments'); fetchComments(); }}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === 'comments'
                ? 'bg-forest text-cream'
                : 'bg-white text-forest hover:bg-stone-100'
            }`}
            data-testid="comments-tab"
          >
            <MessageCircle size={16} />
            Comments ({comments.length})
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
          <input
            type="text"
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-stone-300 rounded-xl pl-12 pr-4 py-3 text-forest focus:outline-none focus:border-terracotta"
          />
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-stone-500 font-body text-sm">Total Users</p>
                <p className="font-heading text-2xl text-forest">{users.length}</p>
              </div>
              {programs.map((program) => (
                <div key={program.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <p className="text-stone-500 font-body text-xs line-clamp-1">{program.title}</p>
                  <p className="font-heading text-2xl text-forest">
                    {users.filter((u) => u.unlocked_programs?.includes(program.id)).length}
                  </p>
                  <p className="text-stone-400 font-body text-xs">unlocked</p>
                </div>
              ))}
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-heading text-xl text-forest">Registered Users <span className="text-stone-400 font-body text-sm">({filteredUsers.length})</span></h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setAllUsersExpanded((v) => !v); setExpandedUsers({}); }}
                    className="px-3 py-2 bg-stone-100 text-forest rounded-lg font-body text-sm hover:bg-stone-200 transition-colors flex items-center gap-2"
                    data-testid="toggle-expand-all-users"
                  >
                    {allUsersExpanded ? <><ChevronUp size={16} /> Collapse all</> : <><ChevronDown size={16} /> Expand all</>}
                  </button>
                  <button
                    onClick={() => setShowAddUser(true)}
                    className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors flex items-center gap-2"
                  >
                    <UserPlus size={16} />
                    Add User
                  </button>
                </div>
              </div>

              {filteredUsers.length === 0 ? (
                <div className="p-8 text-center">
                  <User size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">
                    {searchTerm ? 'No users found matching your search' : 'No users registered yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredUsers.map((user) => (
                    <div key={user.id} className="p-6">
                      <button
                        type="button"
                        onClick={() => toggleUserExpanded(user.email)}
                        className="w-full flex items-center justify-between gap-3 text-left"
                        data-testid={`user-row-${user.email}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <User size={20} className="text-terracotta" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-body font-medium text-forest truncate">
                              {user.name}{user.is_admin ? ' · admin' : ''}
                              {(user.active ?? true) === false && (
                                <span className="ml-2 px-2 py-0.5 rounded-full bg-stone-200 text-stone-500 font-body text-[10px] align-middle">Inactive</span>
                              )}
                            </p>
                            <p className="font-body text-sm text-stone-500 flex items-center gap-1 truncate">
                              <Mail size={12} className="flex-shrink-0" />
                              {user.email}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-body text-xs whitespace-nowrap">
                            {(user.unlocked_programs?.length || 0)} unlocked
                          </span>
                          {(allUsersExpanded || expandedUsers[user.email])
                            ? <ChevronUp size={18} className="text-stone-400" />
                            : <ChevronDown size={18} className="text-stone-400" />}
                        </div>
                      </button>

                      {(allUsersExpanded || expandedUsers[user.email]) && (
                      <div className="mt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {programs.map((program) => {
                          const isUnlocked = user.unlocked_programs?.includes(program.id);
                          return (
                            <button
                              key={program.id}
                              onClick={() => toggleProgramAccess(user.email, program.id, isUnlocked)}
                              className={`px-3 py-2 rounded-lg text-xs font-body transition-colors flex items-center gap-1 justify-center ${
                                isUnlocked
                                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                  : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                              }`}
                            >
                              {isUnlocked ? <Unlock size={12} /> : <Lock size={12} />}
                              <span className="line-clamp-1">{program.title.split(' ').slice(0, 2).join(' ')}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Admin Actions: Reset Password & Delete */}
                      <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap items-center gap-2">
                        {resetPasswordFor === user.email ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={newPasswordValue}
                              onChange={(e) => setNewPasswordValue(e.target.value)}
                              placeholder="New password (min 6 chars)"
                              className="flex-1 px-3 py-1.5 border border-stone-200 rounded-lg font-body text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/30"
                              data-testid={`reset-pw-input-${user.email}`}
                            />
                            <button
                              onClick={() => handleResetPassword(user.email)}
                              className="px-3 py-1.5 bg-terracotta text-white rounded-lg font-body text-xs hover:bg-terracotta-dark transition-colors"
                              data-testid={`reset-pw-confirm-${user.email}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => { setResetPasswordFor(null); setNewPasswordValue(''); }}
                              className="px-2 py-1.5 bg-stone-100 text-stone-500 rounded-lg font-body text-xs hover:bg-stone-200 transition-colors"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : deleteConfirmFor === user.email ? (
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-red-600 font-body text-xs">Delete this user permanently?</span>
                            <button
                              onClick={() => handleDeleteUser(user.email)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg font-body text-xs hover:bg-red-600 transition-colors"
                              data-testid={`delete-confirm-${user.email}`}
                            >
                              Yes, Delete
                            </button>
                            <button
                              onClick={() => setDeleteConfirmFor(null)}
                              className="px-2 py-1.5 bg-stone-100 text-stone-500 rounded-lg font-body text-xs hover:bg-stone-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setResetPasswordFor(user.email)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg font-body text-xs hover:bg-amber-100 transition-colors"
                              data-testid={`reset-pw-btn-${user.email}`}
                            >
                              <KeyRound size={12} />
                              Reset Password
                            </button>
                            <button
                              onClick={() => setDeleteConfirmFor(user.email)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg font-body text-xs hover:bg-red-100 transition-colors"
                              data-testid={`delete-btn-${user.email}`}
                            >
                              <Trash2 size={12} />
                              Delete User
                            </button>
                            <button
                              onClick={() => openJournal(user)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-forest/10 text-forest rounded-lg font-body text-xs hover:bg-forest/20 transition-colors"
                              data-testid={`journal-btn-${user.email}`}
                            >
                              <NotebookPen size={12} />
                              Journal
                            </button>
                            {(user.active ?? true) === false ? (
                              <button
                                onClick={() => handleSetUserActive(user.email, true)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-body text-xs hover:bg-emerald-100 transition-colors"
                                data-testid={`reactivate-btn-${user.email}`}
                                title="Restore login and full access"
                              >
                                <Unlock size={12} />
                                Reactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSetUserActive(user.email, false)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-stone-100 text-stone-600 rounded-lg font-body text-xs hover:bg-stone-200 transition-colors"
                                data-testid={`deactivate-btn-${user.email}`}
                                title="Block login but keep the account and all history"
                              >
                                <Lock size={12} />
                                Deactivate
                              </button>
                            )}
                            {user.unlocked_programs?.includes('fascia-reset') && (
                              <>
                                <button
                                  onClick={() => adminRestartProgram(user.id, user.name, 'fascia-reset', '28 Day Stress Reset')}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-stone-100 text-forest rounded-lg font-body text-xs hover:bg-stone-200 transition-colors"
                                  data-testid={`restart-fascia-${user.email}`}
                                  title="Relock all days and make today Day 1"
                                >
                                  <RotateCcw size={12} />
                                  Restart 28-Day
                                </button>
                                <button
                                  onClick={() => adminSetStartDate(user.email, user.name, 'fascia-reset', '28 Day Stress Reset')}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-800 rounded-lg font-body text-xs hover:bg-amber-100 transition-colors"
                                  data-testid={`set-start-date-${user.email}`}
                                  title="Backdate to unlock all days · let user self-pace"
                                >
                                  <CalendarClock size={12} />
                                  Set Start Date
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl text-forest">Newsletter Subscribers</h2>
                  <p className="text-stone-500 font-body text-sm">{subscribers.length} subscribers</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyEmailsToClipboard}
                    className="px-4 py-2 bg-stone-100 text-forest rounded-lg font-body text-sm hover:bg-stone-200 transition-colors"
                  >
                    Copy All Emails
                  </button>
                  <button
                    onClick={() => downloadCSV(subscribers, 'newsletter_subscribers.csv')}
                    className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors flex items-center gap-2"
                  >
                    <Download size={14} />
                    Download CSV
                  </button>
                </div>
              </div>

              {filteredSubscribers.length === 0 ? (
                <div className="p-8 text-center">
                  <Newspaper size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">
                    {searchTerm ? 'No subscribers found' : 'No subscribers yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredSubscribers.map((sub, index) => (
                    <div key={index} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                          <Mail size={18} className="text-terracotta" />
                        </div>
                        <div>
                          <p className="font-body text-forest">{sub.email}</p>
                          <p className="font-body text-xs text-stone-400">
                            Source: {sub.source || 'unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-body text-xs text-stone-400">
                          {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : ''}
                        </p>
                        <button
                          onClick={() => handleDeleteSubscriber(sub.email)}
                          className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove from newsletter"
                          data-testid={`delete-subscriber-${sub.email}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Quiz Results Tab */}
        {activeTab === 'quizzes' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-xl text-forest">Quiz Results</h2>
                  <p className="text-stone-500 font-body text-sm">{quizResults.length} submissions</p>
                </div>
                <button
                  onClick={() => downloadCSV(quizResults, 'quiz_results.csv')}
                  className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors flex items-center gap-2"
                >
                  <Download size={14} />
                  Download CSV
                </button>
              </div>

              {filteredQuizResults.length === 0 ? (
                <div className="p-8 text-center">
                  <ClipboardList size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">
                    {searchTerm ? 'No results found' : 'No quiz submissions yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredQuizResults.map((result, index) => (
                    <div key={index} className="px-6 py-4">
                      <button
                        onClick={() => toggleResultExpand(index)}
                        className="w-full flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                            {getResultIcon(result.quiz_type, result.result)}
                          </div>
                          <div className="text-left">
                            <p className="font-body text-forest">{result.email}</p>
                            <p className="font-body text-xs text-stone-400">
                              {quizTypeLabel(result.quiz_type)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-body text-sm text-forest font-medium">
                              {getResultLabel(result.quiz_type, result.result)}
                            </p>
                            <p className="font-body text-xs text-stone-400">
                              {result.created_at ? new Date(result.created_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                          {expandedResults[index] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </button>

                      <div className="flex justify-between items-center mt-2">
                        <label
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 cursor-pointer select-none group"
                          data-testid={`results-sent-label-${result.quiz_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!result.results_sent}
                            onChange={(e) => { e.stopPropagation(); toggleResultsSent(result.quiz_id, e.target.checked); }}
                            className="w-4 h-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-600"
                            data-testid={`results-sent-checkbox-${result.quiz_id}`}
                          />
                          <span className={`text-xs font-body ${result.results_sent ? 'text-emerald-700 font-medium' : 'text-stone-500'}`}>
                            Results sent
                            {result.results_sent && result.results_sent_at && (
                              <span className="text-stone-400 font-normal"> · {new Date(result.results_sent_at).toLocaleDateString()}</span>
                            )}
                          </span>
                        </label>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteQuizResult(result.quiz_id, result.email); }}
                          className="flex items-center gap-1 px-2.5 py-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs font-body"
                          title="Delete this quiz submission"
                          data-testid={`delete-quiz-${result.quiz_id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>

                      <AnimatePresence>
                        {expandedResults[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 space-y-4">
                              {/* Wellness Pattern Assessment — detailed scores + responses */}
                              {result.quiz_type === 'wellness_pattern_assessment' && result.answers && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setWorksheetResult(result)}
                                      className="inline-flex items-center gap-2 bg-forest text-cream px-4 py-2 rounded-full text-sm font-body font-medium hover:bg-forest-dark transition-colors"
                                      data-testid="open-worksheet-btn"
                                    >
                                      <ClipboardList size={14} /> Open Practitioner Worksheet
                                    </button>
                                    {result.practitioner_notes && (
                                      <span className="text-xs font-body text-emerald-600 inline-flex items-center gap-1">
                                        <Check size={12} /> Notes saved
                                      </span>
                                    )}
                                  </div>
                                  {result.answers.scores && (
                                    <div className="bg-stone-50 rounded-xl p-4">
                                      <h4 className="font-body font-medium text-forest mb-3 text-sm">Section Scores</h4>
                                      <div className="space-y-1.5">
                                        {Object.entries(result.answers.scores).map(([name, info]) => (
                                          <div key={name} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                            <span className="text-sm font-body text-forest">{name}</span>
                                            <span className="text-sm font-body font-medium text-terracotta">{info.score}/20 · {info.band}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {result.answers.reflection && (
                                    <div className="bg-stone-50 rounded-xl p-4">
                                      <h4 className="font-body font-medium text-forest mb-1 text-sm">Final Reflection</h4>
                                      <p className="text-sm font-body text-stone-600 italic">&ldquo;{result.answers.reflection}&rdquo;</p>
                                    </div>
                                  )}
                                  {result.answers.responses && Object.keys(result.answers.responses).length > 0 && (
                                    <div className="bg-stone-50 rounded-xl p-4">
                                      <h4 className="font-body font-medium text-forest mb-3 text-sm">Responses</h4>
                                      <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-2">
                                        {bodySections.map((sec, si) => (
                                          <div key={sec.key}>
                                            <p className="text-xs font-body font-semibold text-terracotta uppercase tracking-wide mb-1.5">{sec.name}</p>
                                            <div className="space-y-1.5">
                                              {sec.questions.map((qText, qi) => {
                                                const flat = si * 5 + qi;
                                                const val = result.answers.responses[flat];
                                                return (
                                                  <div key={flat} className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                                    <span className="text-sm font-body text-stone-600">{qText}</span>
                                                    <span className="text-sm font-body font-medium text-forest text-right flex-shrink-0 whitespace-nowrap">{val !== undefined ? `${bodyScaleLabel(val)} (${val})` : '—'}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ))}
                                        {/* Optional deeper-dive responses */}
                                        <div>
                                          <p className="text-xs font-body font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                                            {bodyDeeperDive.name}
                                            {result.answers.deeperDive && ` · ${result.answers.deeperDive.score}/${result.answers.deeperDive.max}`}
                                          </p>
                                          <div className="space-y-1.5">
                                            {bodyDeeperDive.questions.map((qText, qi) => {
                                              const flat = bodySections.length * 5 + qi;
                                              const val = result.answers.responses[flat];
                                              return (
                                                <div key={flat} className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                                  <span className="text-sm font-body text-stone-600">{qText}</span>
                                                  <span className="text-sm font-body font-medium text-forest text-right flex-shrink-0 whitespace-nowrap">{val !== undefined ? `${bodyScaleLabel(val)} (${val})` : '—'}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                      <p className="text-xs text-stone-400 font-body italic mt-3">Scale: 0 = Never · 1 = Rarely · 2 = Sometimes · 3 = Often · 4 = Almost Always. Deeper-dive questions are optional and not scored into the bands.</p>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* Metabolic Quiz — rhythm tally + worksheet + readable responses */}
                              {result.quiz_type === 'metabolic_rhythm' && result.answers && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setWorksheetResult(result)}
                                      className="inline-flex items-center gap-2 bg-forest text-cream px-4 py-2 rounded-full text-sm font-body font-medium hover:bg-forest-dark transition-colors"
                                      data-testid="open-worksheet-btn"
                                    >
                                      <ClipboardList size={14} /> Open Practitioner Worksheet
                                    </button>
                                    {result.practitioner_notes && (
                                      <span className="text-xs font-body text-emerald-600 inline-flex items-center gap-1">
                                        <Check size={12} /> Notes saved
                                      </span>
                                    )}
                                  </div>
                                  <div className="bg-stone-50 rounded-xl p-4" data-testid="admin-metabolic-tally">
                                    <h4 className="font-body font-medium text-forest mb-3 text-sm">Rhythm Tally</h4>
                                    <div className="grid grid-cols-3 gap-3">
                                      {['A', 'B', 'C'].map((letter) => {
                                        const count = Object.values(result.answers).filter((v) => v === letter).length;
                                        return (
                                          <div key={letter} className="bg-white border border-stone-100 rounded-lg px-3 py-3 text-center">
                                            <p className="font-heading text-xl text-forest">{count}</p>
                                            <p className="font-body text-[11px] text-stone-500 leading-tight mt-1">{metabolicLetterMeaning[letter]}</p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div className="bg-stone-50 rounded-xl p-4">
                                    <h4 className="font-body font-medium text-forest mb-3 text-sm">Responses</h4>
                                    <div className="space-y-1.5 max-h-[28rem] overflow-y-auto pr-2">
                                      {metabolicQuestions.map((mq, i) => {
                                        const val = result.answers[i];
                                        const answerText = val ? `${mq[val]} (${val})` : '—';
                                        return (
                                          <div key={i} className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                            <span className="text-sm font-body text-stone-600">{i + 1}. {mq.q}</span>
                                            <span className="text-sm font-body font-medium text-forest text-right flex-shrink-0 max-w-[45%]">{answerText}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Lifestyle / Vitality Potential — section scores + worksheet + readable responses */}
                              {(result.quiz_type === 'lifestyle_assessment' || result.quiz_type === 'lifestyle-assessment') && result.answers && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setWorksheetResult(result)}
                                      className="inline-flex items-center gap-2 bg-forest text-cream px-4 py-2 rounded-full text-sm font-body font-medium hover:bg-forest-dark transition-colors"
                                      data-testid="open-worksheet-btn"
                                    >
                                      <ClipboardList size={14} /> Open Practitioner Worksheet
                                    </button>
                                    {result.practitioner_notes && (
                                      <span className="text-xs font-body text-emerald-600 inline-flex items-center gap-1">
                                        <Check size={12} /> Notes saved
                                      </span>
                                    )}
                                  </div>
                                  <div className="bg-stone-50 rounded-xl p-4">
                                    <h4 className="font-body font-medium text-forest mb-3 text-sm">Section Scores</h4>
                                    <div className="space-y-1.5">
                                      {lifestyleDomains.map((d) => {
                                        const max = d.questions.length * 8;
                                        let score = 0;
                                        d.questions.forEach((q) => {
                                          if (result.answers[q.id] !== undefined) score += scoreLifestyleValue(result.answers[q.id], q.reverse);
                                        });
                                        const status = getLifestyleDomainStatus(score, max);
                                        return (
                                          <div key={d.id} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                            <span className="text-sm font-body text-forest">{d.icon} {d.title}</span>
                                            <span className={`text-sm font-body font-medium ${lifestyleStatusColor(status)}`}>{score}/{max} · {status}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div className="bg-stone-50 rounded-xl p-4">
                                    <h4 className="font-body font-medium text-forest mb-3 text-sm">Responses</h4>
                                    <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-2">
                                      {lifestyleDomains.map((d) => (
                                        <div key={d.id}>
                                          <p className="text-xs font-body font-semibold text-terracotta uppercase tracking-wide mb-1.5">{d.icon} {d.title}</p>
                                          <div className="space-y-1.5">
                                            {d.questions.map((q) => {
                                              const val = result.answers[q.id];
                                              return (
                                                <div key={q.id} className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100">
                                                  <span className="text-sm font-body text-stone-600">{q.text}{q.reverse ? ' (reverse-scored)' : ''}</span>
                                                  <span className="text-sm font-body font-medium text-forest text-right flex-shrink-0 whitespace-nowrap">{val !== undefined ? `${lifestyleAnswerLabels[val]} (${val})` : '—'}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-xs text-stone-400 font-body italic mt-3">Scale: 0 = Never · 1 = Rarely · 3 = Sometimes · 5 = Often · 8 = Consistently. Reverse-scored items are flipped when totalling load.</p>
                                  </div>
                                </>
                              )}

                              {/* Food Preferences */}
                              {result.quiz_type === 'food_preferences' && result.answers && (
                                <div className="space-y-4" data-testid="admin-food-prefs">
                                  <div className="bg-emerald-50 rounded-xl p-4">
                                    <h4 className="font-body font-medium text-emerald-800 mb-2 text-sm flex items-center gap-1.5">
                                      <Check size={15} /> Loves / can eat ({(result.answers.love || []).length})
                                    </h4>
                                    {(result.answers.love || []).length ? (
                                      <div className="flex flex-wrap gap-1.5">
                                        {result.answers.love.map((f, i) => (
                                          <span key={i} className="bg-white border border-emerald-200 text-emerald-700 text-xs font-body rounded-full px-2.5 py-1">{f}</span>
                                        ))}
                                      </div>
                                    ) : <p className="text-xs font-body text-stone-400 italic">None selected.</p>}
                                  </div>
                                  <div className="bg-stone-100 rounded-xl p-4">
                                    <h4 className="font-body font-medium text-stone-600 mb-2 text-sm flex items-center gap-1.5">
                                      <X size={15} /> Avoid / can't eat ({(result.answers.avoid || []).length})
                                    </h4>
                                    {(result.answers.avoid || []).length ? (
                                      <div className="flex flex-wrap gap-1.5">
                                        {result.answers.avoid.map((f, i) => (
                                          <span key={i} className="bg-white border border-stone-300 text-stone-500 text-xs font-body rounded-full px-2.5 py-1 line-through">{f}</span>
                                        ))}
                                      </div>
                                    ) : <p className="text-xs font-body text-stone-400 italic">None selected.</p>}
                                  </div>
                                  {result.answers.notes && (
                                    <div className="bg-[#f5efe4] border border-[#e8dcc3] rounded-xl p-4">
                                      <h4 className="font-body font-medium text-[#8a6a5a] mb-1 text-sm">Notes / other foods</h4>
                                      <p className="text-sm font-body text-stone-700 whitespace-pre-wrap">{result.answers.notes}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Per-question answers — fallback for any other quiz type */}
                              {result.quiz_type !== 'wellness_pattern_assessment' && result.quiz_type !== 'metabolic_rhythm' && result.quiz_type !== 'lifestyle_assessment' && result.quiz_type !== 'lifestyle-assessment' && result.quiz_type !== 'food_preferences' && result.answers && Object.keys(result.answers).length > 0 && (
                                <div className="bg-stone-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-3 text-sm">Answers ({Object.keys(result.answers).length} questions)</h4>
                                  <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
                                    {Object.entries(result.answers)
                                      .sort((a, b) => String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true }))
                                      .map(([qKey, value]) => (
                                        <div
                                          key={qKey}
                                          className="flex items-start justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-stone-100"
                                        >
                                          <span className="text-xs font-mono text-stone-400 flex-shrink-0">Q{qKey}</span>
                                          <span className="text-sm font-body text-forest text-right font-medium">{String(value)}</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}

                              <div className="p-4 bg-stone-50 rounded-xl">
                                <h4 className="font-body font-medium text-forest mb-2 text-sm">Full Result Data</h4>
                                <pre className="text-xs text-stone-600 bg-white p-3 rounded-lg overflow-x-auto max-h-72">
                                  {typeof result.result === 'string' && result.result.includes('{') 
                                    ? JSON.stringify(JSON.parse(result.result), null, 2)
                                    : result.result}
                                </pre>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200">
                <h2 className="font-heading text-xl text-forest">User Activity</h2>
                <p className="text-stone-500 font-body text-sm">{userActivity.length} active users tracked</p>
              </div>

              {userActivity.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">No user activity recorded yet</p>
                  <p className="text-stone-400 font-body text-sm mt-1">Activity will appear here as users view lessons and complete snapshots</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {userActivity
                    .filter(u => !searchTerm || u.email.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((userSummary, index) => (
                    <div key={index} className="px-6 py-4">
                      <button
                        onClick={() => toggleResultExpand(`activity-${index}`)}
                        className="w-full flex items-center justify-between"
                        data-testid={`activity-user-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                            <User size={18} className="text-violet-600" />
                          </div>
                          <div className="text-left">
                            <p className="font-body text-forest">{userSummary.email}</p>
                            <p className="font-body text-xs text-stone-400">
                              {userSummary.total_activities} activities · {userSummary.lessons_viewed?.length || 0} lessons · {userSummary.videos_played?.length || 0} videos · {Math.round((userSummary.total_time_seconds || 0) / 60)}min
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-body text-xs text-stone-400">Last active</p>
                            <p className="font-body text-sm text-forest">
                              {userSummary.last_active ? new Date(userSummary.last_active).toLocaleDateString() : 'Unknown'}
                            </p>
                          </div>
                          {expandedResults[`activity-${index}`] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedResults[`activity-${index}`] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 space-y-3">
                              {/* Lessons Viewed */}
                              {userSummary.lessons_viewed?.length > 0 && (
                                <div className="bg-stone-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-2 text-sm">Lessons Viewed ({userSummary.lessons_viewed.length}):</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {userSummary.lessons_viewed.map((lesson, i) => (
                                      <span key={i} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-body border border-emerald-200">
                                        {lesson}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Videos Played */}
                              {userSummary.videos_played?.length > 0 && (
                                <div className="bg-stone-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-2 text-sm">Videos Played ({userSummary.videos_played.length}):</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {userSummary.videos_played.map((vid, i) => (
                                      <span key={i} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-body border border-blue-200">
                                        {vid}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Completions */}
                              {userSummary.completions?.length > 0 && (
                                <div className="bg-stone-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-2 text-sm">Marked Complete ({userSummary.completions.length}):</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {userSummary.completions.map((c, i) => (
                                      <span key={i} className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-body border border-amber-200">
                                        {c}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Links Clicked */}
                              {userSummary.links_clicked?.length > 0 && (
                                <div className="bg-stone-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-2 text-sm">Links Clicked ({userSummary.links_clicked.length}):</h4>
                                  <div className="space-y-1">
                                    {userSummary.links_clicked.map((link, i) => (
                                      <div key={i} className="flex items-center gap-2 text-xs font-body">
                                        <span className="text-terracotta">&#8599;</span>
                                        <span className="text-stone-600">{link.title}</span>
                                        <span className="text-stone-400 truncate max-w-[200px]">{link.url}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Time Summary */}
                              {userSummary.total_time_seconds > 0 && (
                                <div className="bg-violet-50 rounded-xl p-4">
                                  <h4 className="font-body font-medium text-forest mb-1 text-sm">Total Time Tracked</h4>
                                  <p className="text-violet-700 font-heading text-lg">{Math.round(userSummary.total_time_seconds / 60)} minutes</p>
                                </div>
                              )}

                              {/* Activity Timeline */}
                              <div className="bg-stone-50 rounded-xl p-4">
                                <h4 className="font-body font-medium text-forest mb-2 text-sm">Recent Activity:</h4>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {userSummary.activities.slice(0, 30).map((act, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                        act.activity_type === 'video_play' ? 'bg-blue-500' :
                                        act.activity_type === 'link_click' ? 'bg-terracotta' :
                                        act.activity_type === 'mark_complete' ? 'bg-amber-500' :
                                        act.activity_type === 'time_spent' ? 'bg-violet-500' :
                                        act.activity_type === 'section_view' ? 'bg-teal-500' :
                                        'bg-emerald-500'
                                      }`} />
                                      <span className="text-stone-600 font-body flex-1">
                                        {act.activity_type === 'lesson_view' && `Viewed: ${act.item_title}`}
                                        {act.activity_type === 'video_play' && `Played video: ${act.item_title}`}
                                        {act.activity_type === 'link_click' && `Clicked: ${act.item_title}`}
                                        {act.activity_type === 'mark_complete' && `Completed: ${act.item_title}`}
                                        {act.activity_type === 'section_view' && `Section: ${act.item_title}`}
                                        {act.activity_type === 'time_spent' && `Spent ${act.duration_seconds || 0}s on ${act.item_title}`}
                                        {act.activity_type === 'wellness_snapshot' && `Snapshot: ${act.item_title}`}
                                        {act.activity_type === 'quiz_complete' && `Quiz: ${act.item_title}`}
                                      </span>
                                      <span className="text-stone-400 font-body text-xs flex-shrink-0">
                                        {act.created_at ? new Date(act.created_at).toLocaleString() : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* New Client Intakes Tab */}
        {activeTab === 'intakes' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="intakes-panel">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-xl text-forest">New Client Intakes</h2>
                  <p className="text-stone-500 font-body text-sm">
                    {filteredIntakes.length} of {intakes.length} submission{intakes.length === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  onClick={() => downloadCSV(intakes, 'new_client_intakes.csv')}
                  disabled={intakes.length === 0}
                  className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="intakes-export-csv"
                >
                  <Download size={14} />
                  Download CSV
                </button>
              </div>

              {filteredIntakes.length === 0 ? (
                <div className="p-10 text-center">
                  <UserPlus size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">
                    {searchTerm ? 'No intakes match your search' : 'No new client intakes yet'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredIntakes.map((intake) => {
                    const isOpen = !!expandedResults[intake.id];
                    const isUnread = !intake.read;
                    return (
                      <div key={intake.id} className="px-6 py-4" data-testid={`intake-row-${intake.id}`}>
                        <button
                          onClick={() => {
                            toggleResultExpand(intake.id);
                            if (!isOpen && isUnread) markIntakeRead(intake.id);
                          }}
                          className="w-full flex items-center justify-between gap-4 text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="relative w-10 h-10 bg-[#f1e8dd] rounded-full flex items-center justify-center flex-shrink-0">
                              <UserPlus size={18} className="text-[#8a6a5a]" />
                              {isUnread && (
                                <span
                                  className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-terracotta rounded-full border-2 border-white"
                                  data-testid={`intake-unread-dot-${intake.id}`}
                                  aria-label="unread"
                                />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-body text-forest truncate">
                                <span className={isUnread ? 'font-semibold' : 'font-medium'}>{intake.name}</span>
                                <span className="text-stone-400 font-normal"> · {intake.email}</span>
                                {isUnread && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider text-terracotta font-body font-semibold">New</span>
                                )}
                              </p>
                              <p className="font-body text-xs text-stone-400 truncate">
                                {intake.phone ? `${intake.phone} · ` : ''}
                                {intake.heard_from ? `Heard via ${intake.heard_from}` : 'Source not provided'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <p className="font-body text-xs text-stone-400 hidden sm:block">
                              {intake.submitted_at ? new Date(intake.submitted_at).toLocaleDateString() : ''}
                            </p>
                            {isOpen ? (
                              <ChevronUp size={16} className="text-stone-400" />
                            ) : (
                              <ChevronDown size={16} className="text-stone-400" />
                            )}
                          </div>
                        </button>

                        {isOpen && (
                          <div className="mt-4 pl-13 grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#faf6ef] rounded-xl p-4 border border-stone-100">
                            <div>
                              <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Goals</p>
                              <p className="text-sm font-body text-stone-700 whitespace-pre-wrap">
                                {intake.goals || <span className="italic text-stone-400">—</span>}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Experience</p>
                              <p className="text-sm font-body text-stone-700 whitespace-pre-wrap">
                                {intake.experience || <span className="italic text-stone-400">—</span>}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Waiver Consent</p>
                              <p className="text-sm font-body text-stone-700">
                                {intake.consent_waiver ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    <Check size={14} /> Agreed
                                  </span>
                                ) : (
                                  <span className="text-stone-400">Not yet</span>
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Submitted</p>
                              <p className="text-sm font-body text-stone-700">
                                {intake.submitted_at ? new Date(intake.submitted_at).toLocaleString() : '—'}
                              </p>
                            </div>
                            <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
                              <a
                                href={`mailto:${intake.email}?subject=${encodeURIComponent('Welcome — Grounded Wellness with Jill')}`}
                                className="inline-flex items-center gap-1.5 text-sm font-body text-forest underline hover:text-forest-dark"
                              >
                                <Mail size={14} /> Email {intake.name.split(' ')[0]}
                              </a>
                              {intake.phone && (
                                <a
                                  href={`tel:${intake.phone}`}
                                  className="inline-flex items-center gap-1.5 text-sm font-body text-forest underline hover:text-forest-dark"
                                >
                                  Call {intake.phone}
                                </a>
                              )}
                              <button
                                onClick={() => handleDeleteIntake(intake.id, intake.name)}
                                className="ml-auto inline-flex items-center gap-1.5 text-sm font-body text-stone-400 hover:text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-lg transition-colors"
                                data-testid={`delete-intake-${intake.id}`}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="inquiries-panel">
            {inquiries.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
                <p className="text-stone-500 font-body">No inquiries yet — when visitors submit the form, they&apos;ll appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {inquiries.map((inq) => (
                  <div
                    key={inq.id}
                    className={`bg-white rounded-2xl p-5 shadow-soft border-2 transition-colors ${
                      inq.responded ? 'border-forest/30 opacity-70' : 'border-terracotta/40'
                    }`}
                    data-testid={`inquiry-${inq.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading text-lg text-forest">{inq.name}</h3>
                          {!inq.responded && (
                            <span className="text-[10px] uppercase tracking-wider font-body font-semibold px-2 py-0.5 rounded-full bg-terracotta text-white">New</span>
                          )}
                          {inq.responded && (
                            <span className="text-[10px] uppercase tracking-wider font-body font-semibold px-2 py-0.5 rounded-full bg-forest/20 text-forest">Responded</span>
                          )}
                          {inq.info_session_interest && (
                            <span className="text-[10px] uppercase tracking-wider font-body font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Info Session</span>
                          )}
                        </div>
                        <p className="font-body text-sm text-stone-600">
                          <a href={`mailto:${inq.email}`} className="text-forest hover:underline">{inq.email}</a>
                          {inq.phone && <span> · {inq.phone}</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-body text-xs text-stone-500">{new Date(inq.created_at).toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-2 mb-3">
                      {inq.program && (
                        <div>
                          <p className="text-[10px] font-body text-stone-500 uppercase tracking-wider">Program</p>
                          <p className="text-sm font-body text-forest">{inq.program}</p>
                        </div>
                      )}
                      {inq.tier && (
                        <div>
                          <p className="text-[10px] font-body text-stone-500 uppercase tracking-wider">Tier</p>
                          <p className="text-sm font-body text-forest">{inq.tier}</p>
                        </div>
                      )}
                    </div>

                    {inq.message && (
                      <div className="bg-cream rounded-xl p-3 mb-3">
                        <p className="text-[10px] font-body text-stone-500 uppercase tracking-wider mb-1">Message</p>
                        <p className="font-body text-sm text-stone-700 whitespace-pre-line">{inq.message}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => toggleInquiryResponded(inq.id)}
                        data-testid={`toggle-responded-${inq.id}`}
                        className="text-xs font-body px-3 py-1.5 rounded-full bg-forest text-cream hover:bg-forest-dark transition-colors"
                      >
                        {inq.responded ? 'Mark as New' : 'Mark as Responded'}
                      </button>
                      <a
                        href={`mailto:${inq.email}?subject=${encodeURIComponent(`Re: ${inq.program || 'Your inquiry'}`)}`}
                        className="text-xs font-body px-3 py-1.5 rounded-full bg-white border border-stone-300 text-forest hover:bg-stone-50 transition-colors"
                      >
                        Reply via Email
                      </a>
                      <button
                        onClick={() => deleteInquiry(inq.id)}
                        data-testid={`delete-inquiry-${inq.id}`}
                        className="text-xs font-body px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-600 hover:bg-red-50 transition-colors ml-auto"
                      >
                        Delete
                      </button>
                    </div>

                    {inq.source && (
                      <p className="text-[10px] font-body text-stone-400 mt-2">Source: {inq.source}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Zoom Poll Tab */}
        {activeTab === 'poll' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="poll-panel">
            {/* Poll Open/Close Control */}
            <div
              className={`mb-4 rounded-2xl overflow-hidden border ${pollConfig.is_closed ? 'border-forest/40 bg-forest/5' : 'border-stone-200 bg-white'}`}
              data-testid="poll-config-panel"
            >
              <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-3 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-xs font-body font-medium rounded-full px-3 py-1 ${pollConfig.is_closed ? 'bg-forest text-cream' : 'bg-emerald-100 text-emerald-800'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${pollConfig.is_closed ? 'bg-cream' : 'bg-emerald-600'}`} />
                  {pollConfig.is_closed ? 'Poll Closed' : 'Poll Live'}
                </span>
                <p className="text-sm font-body text-stone-600">
                  {pollConfig.is_closed
                    ? 'Voting is blocked; public page shows the final times banner.'
                    : 'Clients can vote and resubmit; results update live.'}
                </p>
              </div>
              <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-stone-400 font-body mb-1.5">
                    Final Times Message (shown when poll is closed)
                  </label>
                  <textarea
                    rows={2}
                    placeholder="e.g. 7:00 AM weekdays · 8:00 AM Saturday · Afternoon Sunday"
                    value={pollConfigDraft.final_message}
                    onChange={(e) => setPollConfigDraft(d => ({ ...d, final_message: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-stone-300 font-body text-sm focus:outline-none focus:border-forest resize-none"
                    data-testid="poll-final-message-input"
                  />
                </div>
                <div className="flex flex-col gap-2 md:items-end">
                  <label className="flex items-center gap-2 text-sm font-body text-forest cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={pollConfigDraft.is_closed}
                      onChange={(e) => setPollConfigDraft(d => ({ ...d, is_closed: e.target.checked }))}
                      className="accent-forest w-4 h-4"
                      data-testid="poll-close-toggle"
                    />
                    Close the poll
                  </label>
                  <button
                    onClick={savePollConfig}
                    disabled={pollConfigSaving || (pollConfigDraft.is_closed === pollConfig.is_closed && pollConfigDraft.final_message === pollConfig.final_message)}
                    className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="poll-save-config"
                  >
                    {pollConfigSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading text-xl text-forest">Zoom Times Poll · 28 Day Reset</h2>
                  <p className="text-stone-500 font-body text-sm">
                    {pollVotes.length} {pollVotes.length === 1 ? 'response' : 'responses'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <a
                    href="/poll/zoom-times"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border border-stone-300 text-forest rounded-lg font-body text-sm hover:border-forest transition-colors flex items-center gap-2"
                  >
                    View Public Poll
                  </a>
                  <button
                    onClick={() => downloadCSV(pollVotes, 'zoom_poll_votes.csv')}
                    disabled={pollVotes.length === 0}
                    className="px-4 py-2 bg-forest text-cream rounded-lg font-body text-sm hover:bg-forest-dark transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="poll-export-csv"
                  >
                    <Download size={14} />
                    Download CSV
                  </button>
                </div>
              </div>

              {pollVotes.length === 0 ? (
                <div className="p-10 text-center">
                  <BarChart3 size={40} className="text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500 font-body">No responses yet.</p>
                  <p className="text-stone-400 font-body text-sm mt-1">
                    Share <code className="bg-stone-100 px-2 py-0.5 rounded">/poll/zoom-times</code> with your community.
                  </p>
                </div>
              ) : (
                <>
                  {/* Aggregate bars */}
                  {(() => {
                    const weekdayCounts = {};
                    const friCounts = {};
                    const satCounts = {};
                    const sunCounts = {};
                    pollVotes.forEach((v) => {
                      (v.weekday_times || []).forEach((t) => { weekdayCounts[t] = (weekdayCounts[t] || 0) + 1; });
                      (v.friday_times || []).forEach((t) => { friCounts[t] = (friCounts[t] || 0) + 1; });
                      (v.saturday_times || []).forEach((t) => { satCounts[t] = (satCounts[t] || 0) + 1; });
                      (v.sunday_times || []).forEach((t) => { sunCounts[t] = (sunCounts[t] || 0) + 1; });
                      // Back-compat: legacy combined weekend_times → treat as Saturday
                      (v.weekend_times || []).forEach((t) => { satCounts[t] = (satCounts[t] || 0) + 1; });
                    });
                    const topWeekday = Object.entries(weekdayCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
                    const topFri = Object.entries(friCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
                    const topSat = Object.entries(satCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
                    const topSun = Object.entries(sunCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
                    const total = pollVotes.length;
                    const renderCol = (title, rows, barClass) => (
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#8a6a5a] font-body mb-3">{title}</p>
                        {rows.length === 0 ? (
                          <p className="text-sm text-stone-400 font-body">—</p>
                        ) : rows.map(([label, count]) => (
                          <div key={label} className="flex items-center gap-3 mb-1.5">
                            <span className="w-28 text-sm font-body text-stone-700">{label}</span>
                            <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                              <div className={`h-full ${barClass} rounded-full`} style={{ width: `${(count / total) * 100}%` }} />
                            </div>
                            <span className="text-xs font-body text-stone-500 w-8 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    );
                    return (
                      <div className="px-6 py-5 border-b border-stone-100 bg-[#faf6ef] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {renderCol('Mon–Thu', topWeekday, 'bg-forest')}
                        {renderCol('Friday', topFri, 'bg-[#4a7a7a]')}
                        {renderCol('Saturday', topSat, 'bg-[#8a6a5a]')}
                        {renderCol('Sunday', topSun, 'bg-[#6a5a78]')}
                      </div>
                    );
                  })()}

                  {/* Individual votes */}
                  <div className="divide-y divide-stone-100">
                    {pollVotes.map((vote) => {
                      const isOpen = !!expandedResults[vote.id];
                      return (
                        <div key={vote.id} className="px-6 py-4" data-testid={`poll-row-${vote.id}`}>
                          <button
                            onClick={() => toggleResultExpand(vote.id)}
                            className="w-full flex items-center justify-between gap-4 text-left"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-[#eef4f4] rounded-full flex items-center justify-center flex-shrink-0">
                                <BarChart3 size={18} className="text-[#4a7a7a]" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-body text-forest truncate">
                                  <span className="font-medium">{vote.name || 'Anonymous'}</span>
                                  {vote.email && (
                                    <span className="text-stone-400 font-normal"> · {vote.email}</span>
                                  )}
                                </p>
                                <p className="font-body text-xs text-stone-400 truncate">
                                  {(() => {
                                    const picks = (vote.weekday_times?.length || 0) + (vote.friday_times?.length || 0) + (vote.saturday_times?.length || 0) + (vote.sunday_times?.length || 0) + (vote.weekend_times?.length || 0);
                                    const suggested = vote.suggested_times ? ' · suggested a time' : '';
                                    return `${picks} time${picks === 1 ? '' : 's'} picked${suggested}`;
                                  })()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <p className="font-body text-xs text-stone-400 hidden sm:block">
                                {vote.submitted_at ? new Date(vote.submitted_at).toLocaleDateString() : ''}
                              </p>
                              {isOpen ? <ChevronUp size={16} className="text-stone-400" /> : <ChevronDown size={16} className="text-stone-400" />}
                            </div>
                          </button>

                          {isOpen && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-[#faf6ef] rounded-xl p-4 border border-stone-100">
                              <div>
                                <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-2">Mon–Thu</p>
                                {vote.weekday_times?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {vote.weekday_times.map((t) => (
                                      <span key={t} className="text-xs font-body bg-forest/10 text-forest rounded-full px-2.5 py-1">{t}</span>
                                    ))}
                                  </div>
                                ) : <p className="text-sm italic text-stone-400 font-body">—</p>}
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-2">Friday</p>
                                {vote.friday_times?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {vote.friday_times.map((t) => (
                                      <span key={t} className="text-xs font-body bg-[#4a7a7a]/10 text-[#4a7a7a] rounded-full px-2.5 py-1">{t}</span>
                                    ))}
                                  </div>
                                ) : <p className="text-sm italic text-stone-400 font-body">—</p>}
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-2">Saturday</p>
                                {(vote.saturday_times?.length > 0 || vote.weekend_times?.length > 0) ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {[...(vote.saturday_times || []), ...(vote.weekend_times || [])].map((t, idx) => (
                                      <span key={`${t}-${idx}`} className="text-xs font-body bg-[#8a6a5a]/10 text-[#8a6a5a] rounded-full px-2.5 py-1">{t}</span>
                                    ))}
                                  </div>
                                ) : <p className="text-sm italic text-stone-400 font-body">—</p>}
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-2">Sunday</p>
                                {vote.sunday_times?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {vote.sunday_times.map((t) => (
                                      <span key={t} className="text-xs font-body bg-[#6a5a78]/10 text-[#6a5a78] rounded-full px-2.5 py-1">{t}</span>
                                    ))}
                                  </div>
                                ) : <p className="text-sm italic text-stone-400 font-body">—</p>}
                              </div>
                              {vote.suggested_times && (
                                <div className="md:col-span-2 lg:col-span-4">
                                  <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Suggested Times</p>
                                  <p className="text-sm font-body text-stone-700 italic whitespace-pre-wrap">&ldquo;{vote.suggested_times}&rdquo;</p>
                                </div>
                              )}
                              {vote.notes && (
                                <div className="md:col-span-2 lg:col-span-4">
                                  <p className="text-xs uppercase tracking-wider text-stone-400 font-body mb-1">Notes</p>
                                  <p className="text-sm font-body text-stone-700 whitespace-pre-wrap">{vote.notes}</p>
                                </div>
                              )}
                              {vote.email && (
                                <div className="md:col-span-2 lg:col-span-4">
                                  <a
                                    href={`mailto:${vote.email}?subject=${encodeURIComponent('28 Day Reset — Zoom Schedule Update')}`}
                                    className="inline-flex items-center gap-1.5 text-sm font-body text-forest underline hover:text-forest-dark"
                                  >
                                    <Mail size={14} /> Email {(vote.name || '').split(' ')[0] || 'voter'}
                                  </a>
                                </div>
                              )}
                              <div className="md:col-span-2 lg:col-span-4 flex justify-end pt-1">
                                <button
                                  onClick={() => handleDeletePollVote(vote.id, vote.name || vote.email)}
                                  className="inline-flex items-center gap-1.5 text-sm font-body text-stone-400 hover:text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-lg transition-colors"
                                  data-testid={`delete-poll-vote-${vote.id}`}
                                >
                                  <Trash2 size={14} /> Delete vote
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Help Text */}
        <div className="mt-8 bg-forest/5 rounded-xl p-6">
          <h3 className="font-heading text-lg text-forest mb-2">Quick Tips</h3>
          <ul className="text-stone-600 font-body text-sm space-y-2">
            <li>• <strong>Users tab:</strong> Click program buttons to toggle access (green = unlocked)</li>
            <li>• <strong>Newsletter tab:</strong> Copy all emails or download CSV for your email tool</li>
            <li>• <strong>Quiz Results tab:</strong> Click a result to see full details</li>
            <li>• <strong>User Activity tab:</strong> See which lessons each user has viewed and their progress</li>
            <li>• <strong>New Client Intakes tab:</strong> Click a row to see goals, experience, and contact details; export all as CSV</li>
            <li>• <strong>Zoom Poll tab:</strong> Top-picked times at a glance + every individual vote; CSV export ready</li>
          </ul>
        </div>
      </div>

      {/* Add User Modal */}

      {/* Analytics Tab */}
      {activeTab === 'comments' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-comments-panel">
          {comments.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <MessageCircle size={32} className="text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-body">No Flex Detox comments yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {comments
                .filter((c) => {
                  const q = searchTerm.toLowerCase();
                  return !q || (c.user_name || '').toLowerCase().includes(q) ||
                    (c.user_email || '').toLowerCase().includes(q) ||
                    (c.text || '').toLowerCase().includes(q) ||
                    (c.system_name || '').toLowerCase().includes(q);
                })
                .map((c) => (
                  <div key={c.id} className="bg-white rounded-2xl p-5 shadow-sm flex gap-4" data-testid={`admin-comment-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-heading text-forest">{c.user_name}</span>
                        <span className="text-xs font-body text-stone-400">{c.user_email}</span>
                        <span className="text-xs font-body px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{c.system_name}</span>
                        <span className="text-xs font-body text-stone-400">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="font-body text-sm text-stone-700 whitespace-pre-wrap break-words">{c.text}</p>
                    </div>
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="flex-shrink-0 self-start text-stone-300 hover:text-red-500 transition-colors"
                      title="Delete comment"
                      data-testid={`admin-delete-comment-${c.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
            </div>
          )}
        </motion.div>
      )}


      {activeTab === 'analytics' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {analyticsLoading ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <p className="text-stone-500 font-body">Loading analytics...</p>
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <p className="text-stone-400 font-body text-xs uppercase tracking-wide">Total Page Views</p>
                  <p className="text-3xl font-heading text-forest mt-1">{analytics.total_views}</p>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <p className="text-stone-400 font-body text-xs uppercase tracking-wide">Today</p>
                  <p className="text-3xl font-heading text-terracotta mt-1">{analytics.today_views}</p>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <p className="text-stone-400 font-body text-xs uppercase tracking-wide">Pages Tracked</p>
                  <p className="text-3xl font-heading text-forest mt-1">{analytics.pages.length}</p>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <p className="text-stone-400 font-body text-xs uppercase tracking-wide">Last 30 Days</p>
                  <p className="text-3xl font-heading text-forest mt-1">{analytics.daily.reduce((s, d) => s + d.views, 0)}</p>
                </div>
              </div>

              {/* Daily Views Chart */}
              {analytics.daily.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-stone-200">
                    <h2 className="font-heading text-xl text-forest">Daily Views (Last 30 Days)</h2>
                  </div>
                  <div className="p-6">
                    <div className="flex items-end gap-1 h-40">
                      {analytics.daily.map((d, i) => {
                        const max = Math.max(...analytics.daily.map(x => x.views));
                        const height = max > 0 ? (d.views / max) * 100 : 0;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center group relative">
                            <div className="absolute -top-8 bg-forest text-cream text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {d.date.slice(5)}: {d.views} views
                            </div>
                            <div
                              className="w-full bg-terracotta/80 hover:bg-terracotta rounded-t transition-colors min-h-[2px]"
                              style={{ height: `${height}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-stone-400 font-body">{analytics.daily[0]?.date.slice(5)}</span>
                      <span className="text-xs text-stone-400 font-body">{analytics.daily[analytics.daily.length - 1]?.date.slice(5)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Popular Pages */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-xl text-forest">Popular Pages</h2>
                    <p className="text-stone-500 font-body text-sm">Ranked by total views</p>
                  </div>
                  <button
                    onClick={fetchAnalytics}
                    className="text-sm font-body text-terracotta hover:text-terracotta-dark transition-colors"
                  >
                    Refresh
                  </button>
                </div>
                <div className="divide-y divide-stone-100">
                  {analytics.pages.map((p, i) => {
                    const maxViews = analytics.pages[0]?.views || 1;
                    const pageName = p.page === '/' ? 'Home' : p.page.replace(/^\//, '').replace(/\//g, ' / ').replace(/-/g, ' ');
                    return (
                      <div key={i} className="px-6 py-3 flex items-center gap-4">
                        <span className="text-stone-400 font-body text-sm w-6 text-right">{i + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-forest text-sm capitalize truncate">{pageName}</p>
                          <div className="mt-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                            <div className="h-full bg-terracotta/60 rounded-full" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                          </div>
                        </div>
                        <span className="font-body text-sm text-stone-500 font-medium">{p.views}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-stone-200">
                  <h2 className="font-heading text-xl text-forest">Recent Page Views</h2>
                  <p className="text-stone-500 font-body text-sm">Last 20 visits</p>
                </div>
                <div className="divide-y divide-stone-100 max-h-[400px] overflow-y-auto">
                  {analytics.recent.map((r, i) => {
                    const pageName = r.page === '/' ? 'Home' : r.page.replace(/^\//, '').replace(/-/g, ' ');
                    const time = new Date(r.timestamp).toLocaleString();
                    const device = r.screen_width ? (r.screen_width < 768 ? 'Mobile' : r.screen_width < 1024 ? 'Tablet' : 'Desktop') : '—';
                    return (
                      <div key={i} className="px-6 py-3 flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="font-body text-forest text-sm capitalize truncate">{pageName}</p>
                          <p className="text-stone-400 font-body text-xs">{time}</p>
                        </div>
                        <span className="text-xs font-body text-stone-400 bg-stone-100 px-2 py-1 rounded-full flex-shrink-0">{device}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center">
              <BarChart3 size={40} className="text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-body">No analytics data yet</p>
              <p className="text-stone-400 font-body text-sm mt-1">Page views will appear here as visitors browse your site</p>
            </div>
          )}
        </motion.div>
      )}

      {/* Old Add User Modal */}
      <AnimatePresence>
        {showAddUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddUser(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl text-forest">Add New User</h2>
                <button
                  onClick={() => setShowAddUser(false)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-forest font-body font-medium mb-1 text-sm">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    required
                    className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
                    placeholder="Jane Smith"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-forest font-body font-medium mb-1 text-sm">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
                    placeholder="jane@example.com"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-forest font-body font-medium mb-1 text-sm">
                    Password
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 pr-10 text-forest focus:outline-none focus:border-terracotta"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="px-3 py-2 bg-stone-100 text-stone-600 rounded-xl font-body text-sm hover:bg-stone-200 transition-colors whitespace-nowrap"
                    >
                      Generate
                    </button>
                  </div>
                  <p className="text-stone-400 font-body text-xs mt-1">
                    Share this password with the user so they can log in
                  </p>
                </div>

                {/* Programs to Unlock */}
                <div>
                  <label className="block text-forest font-body font-medium mb-2 text-sm">
                    Unlock Programs
                  </label>
                  <div className="space-y-2">
                    {programs.map((program) => (
                      <button
                        key={program.id}
                        type="button"
                        onClick={() => toggleNewUserProgram(program.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors text-left ${
                          newUser.unlocked_programs.includes(program.id)
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${
                          newUser.unlocked_programs.includes(program.id)
                            ? 'bg-emerald-500 text-white'
                            : 'bg-stone-200'
                        }`}>
                          {newUser.unlocked_programs.includes(program.id) && <Check size={14} />}
                        </div>
                        <span className="font-body text-forest text-sm">{program.title}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddUser(false)}
                    className="flex-1 px-4 py-3 bg-stone-100 text-stone-600 rounded-full font-body font-medium hover:bg-stone-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingUser}
                    className="flex-1 px-4 py-3 bg-forest text-cream rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addingUser ? (
                      'Creating...'
                    ) : (
                      <>
                        <UserPlus size={18} />
                        Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {worksheetResult && (
        <PractitionerWorksheet
          result={worksheetResult}
          adminEmail={adminEmail}
          adminPassword={adminPassword}
          onClose={() => setWorksheetResult(null)}
          onSaved={fetchQuizResults}
        />
      )}

      {journalUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setJournalUser(null)} data-testid="admin-journal-modal">
          <div className="bg-cream rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-cream border-b border-stone-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <NotebookPen size={18} className="text-forest" />
                <div>
                  <h3 className="font-heading text-lg text-forest leading-tight">Wellness Journal</h3>
                  <p className="font-body text-xs text-stone-500">{journalUser.name} · {journalUser.email}</p>
                </div>
              </div>
              <button onClick={() => setJournalUser(null)} className="text-stone-400 hover:text-forest" data-testid="admin-journal-close"><X size={20} /></button>
            </div>

            <div className="p-6">
              {journalLoading ? (
                <div className="flex items-center gap-2 text-stone-500 font-body py-8 justify-center"><Loader2 size={18} className="animate-spin" /> Loading…</div>
              ) : journalEntries.length === 0 ? (
                <p className="text-stone-500 font-body text-sm text-center py-8">This client has no journal entries yet.</p>
              ) : (
                <div className="space-y-4">
                  {journalEntries.map((entry) => {
                    const ticked = Object.entries(entry.checklist || {}).filter(([, v]) => v).map(([k]) => k);
                    return (
                      <div key={entry.id} className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`admin-journal-entry-${entry.id}`}>
                        <p className="font-heading text-forest mb-2 flex items-center gap-2"><CalendarClock size={15} /> {entry.date}</p>
                        {ticked.length > 0 && (
                          <div className="mb-3">
                            <p className="text-[10px] font-body uppercase tracking-wider text-stone-400 mb-1">Ticked ({ticked.length})</p>
                            <div className="flex flex-wrap gap-1.5">
                              {ticked.map((id) => (
                                <span key={id} className="text-xs font-body bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                                  {journalItemLabels[id] || id}{entry.item_notes?.[id] ? ` — ${entry.item_notes[id]}` : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {entry.session_notes && (
                          <p className="font-body text-sm text-stone-700 mb-2"><span className="text-stone-400 text-xs uppercase tracking-wider">What we did: </span>{entry.session_notes}</p>
                        )}
                        {entry.next_plan && (
                          <p className="font-body text-sm text-stone-700 mb-3"><span className="text-stone-400 text-xs uppercase tracking-wider">Next plan: </span>{entry.next_plan}</p>
                        )}
                        <div className="border-t border-stone-100 pt-3">
                          <label className="block text-[10px] font-body uppercase tracking-wider text-[#8a6a5a] mb-1">Your note to this client</label>
                          <textarea
                            rows={2}
                            value={noteDrafts[entry.id] ?? ''}
                            onChange={(e) => setNoteDrafts((p) => ({ ...p, [entry.id]: e.target.value }))}
                            placeholder="A note the client will see on this session…"
                            className="w-full font-body text-sm text-stone-700 bg-[#f5efe4] border border-[#e8dcc3] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-terracotta"
                            data-testid={`admin-journal-note-${entry.id}`}
                          />
                          <div className="flex justify-end mt-2">
                            <button
                              onClick={() => savePractitionerNote(entry.id)}
                              className="inline-flex items-center gap-1.5 bg-forest text-cream font-body text-xs px-3 py-1.5 rounded-full hover:bg-forest-dark transition-colors"
                              data-testid={`admin-journal-save-${entry.id}`}
                            >
                              {savingNoteId === entry.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save note
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
